import { RpcOptions, UnaryCall } from '@protobuf-ts/runtime-rpc'
import { AppConnectorActions } from 'lib/actions/apps/app-connector'
import { EditActions, RPCAction } from 'lib/actions/rpc'
import { CancelFunc, emptyCancel } from 'lib/cancel'
import { NotifyClient } from 'lib/notify'
import { convertError, DeleteReturn, GetReturn, ListReturn, Status } from 'lib/rpc'
import { actionInvoke, ActionStatus, Collector, expandValue, unaryInvoke, unaryListCollectSinglePage } from 'lib/rpcts'
import {
    App,
    AppView,
    CreateAppRequest,
    CreateAppResponse,
    DeleteAppRequest,
    GetAppRequest,
    GetAppResponse,
    ListAppsResponse,
    SearchAppsExpandedResponse,
    UpdateAppRequest,
    UpdateAppResponse,
} from 'pbts/c1/api/app/v1/app'
import { AppsClient } from 'pbts/c1/api/app/v1/app.client'
import {
    AppAttributeServiceSearchRequest,
    AppAttributeServiceSearchResponse,
    AppPotentialUserProfileAttribute,
} from 'pbts/c1/api/app/v1/app_attributes'
import { AppAttributeSearchServiceClient } from 'pbts/c1/api/app/v1/app_attributes.client'
import { AppActionsServiceGenerateReportRequest } from 'pbts/c1/api/app/v1/app_report'
import { AppReportActionServiceClient } from 'pbts/c1/api/app/v1/app_report.client'
import { Any } from 'pbts/google/protobuf/any'
import { Empty } from 'pbts/google/protobuf/empty'
import { Dispatch } from 'react'
import { AppOwnersActions } from './app-owners'
import { AppSearchActions } from './app-search'

export enum AppsAction {
    get = 'AppsAction.get',
    update = 'AppsAction.update',
    delete = 'AppsAction.delete',
    create = 'AppsAction.create',
    searchPotentialAttributes = 'AppsAction.searchPotentialAttributes',
}

export type GetAction = {
    type: AppsAction.get
    payload: GetReturn<App>
}

export type UpdateAction = {
    type: AppsAction.update
    payload: GetReturn<App>
}

export type DeleteAction = {
    type: AppsAction.delete
    payload: DeleteReturn
}

export type CreateAction = {
    type: AppsAction.create
    payload: ActionStatus<App>
}

export type AppPotentialAttributeAction = {
    type: AppsAction.searchPotentialAttributes
    payload: ListReturn<AppPotentialUserProfileAttribute>
}

export type AppsActions =
    | UpdateAction
    | GetAction
    | AppSearchActions
    | AppOwnersActions
    | AppConnectorActions
    | DeleteAction
    | CreateAction
    | AppPotentialAttributeAction

export class AppPotentialUserProfileAttributeCollector extends Collector<AppPotentialUserProfileAttribute> {
    push(resp: AppAttributeServiceSearchResponse): void {
        this.buf = this.buf.concat(
            resp.items.map((v): AppPotentialUserProfileAttribute => {
                return v
            }),
        )
    }
}

export const searchAppPotentialAttributes = (
    dispatch: Dispatch<AppPotentialAttributeAction>,
    client: AppAttributeSearchServiceClient | undefined,
    appId: string,
    query: string,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const req = AppAttributeServiceSearchRequest.create({
        appId,
        query,
    })
    const collector = new AppPotentialUserProfileAttributeCollector()
    return unaryListCollectSinglePage<AppAttributeServiceSearchRequest, AppAttributeServiceSearchResponse>(
        req,
        client.search.bind(client),
        collector,
        (payload) => {
            switch (payload.status) {
                case Status.OK:
                    dispatch({
                        type: AppsAction.searchPotentialAttributes,
                        payload: {
                            status: Status.OK,
                            items: collector.buffer(),
                            metadata: payload.metadata,
                        },
                    })
                    break
                default:
                    dispatch({
                        type: AppsAction.searchPotentialAttributes,
                        payload,
                    })
            }
        },
    )
}

export const getApp = (
    dispatch: Dispatch<GetAction>,
    client: AppsClient | undefined,
    objectID: string,
    notifyClient?: NotifyClient,
    includeDeleted?: boolean,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const req = GetAppRequest.create({
        id: objectID,
        // TODO(pquerna): App api needs expand mask
        // expandMask: { paths: ['*'] },
        includeDeleted,
    })
    return unaryInvoke<GetAppRequest, GetAppResponse>(
        req,
        client.get.bind(client),
        (payload) => {
            dispatch(convertGetAppPayload(payload))
        },
        notifyClient,
    )
}

const convertCreateAppPayload = (payload: GetReturn<CreateAppResponse>): CreateAction => {
    switch (payload.status) {
        case Status.OK:
            return {
                type: AppsAction.create,
                payload: {
                    status: payload.status,
                    item: App.create(payload.item?.app ?? {}),
                },
            }
        default:
            return {
                type: AppsAction.create,
                payload,
            }
    }
}

const convertGetAppPayload = (payload: GetReturn<GetAppResponse>): GetAction => {
    switch (payload.status) {
        case Status.OK:
            return {
                type: AppsAction.get,
                payload: {
                    status: payload.status,
                    item: App.create(payload.item?.app ?? {}),
                },
            }
        default:
            return {
                type: AppsAction.get,
                payload,
            }
    }
}

const convertUpdateAppPayload = (resp: UpdateAppResponse): UpdateAction => {
    return {
        type: AppsAction.update,
        payload: {
            status: Status.OK,
            item: App.create(resp.app ?? {}),
        },
    }
}

export const deleteApp = (
    dispatch: Dispatch<DeleteAction>,
    client: AppsClient | undefined,
    objectID: string,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const req = DeleteAppRequest.create({
        id: objectID,
    })
    return unaryInvoke<DeleteAppRequest, Empty>(req, client.delete.bind(client), (payload) => {
        dispatch({
            type: AppsAction.delete,
            payload,
        })
    })
}

// interface is empty because Apps don't implement expanders yet

export interface AppValue extends App {
    parentApp?: AppValue
}

export const expandApp = (msg: App | undefined, _values: Any[]): AppValue => {
    return App.create(msg ?? {})
}

export class AppCollector extends Collector<AppValue> {
    push(resp: ListAppsResponse): void {
        this.buf = this.buf.concat(
            resp.list.map((v): AppValue => {
                return expandApp(v, [])
            }),
        )
    }
}

export const expandAppView = (msg: AppView | undefined, values: Any[]): AppValue => {
    const a: AppValue = App.create(msg?.app ?? {})
    a.parentApp = expandValue<App>(msg?.parentAppPath, App, values)
    return a
}

export class AppViewCollector extends Collector<AppValue> {
    push(resp: SearchAppsExpandedResponse): void {
        this.buf = this.buf.concat(
            resp.list.map((v): AppValue => {
                return expandAppView(v, resp.expanded)
            }),
        )
    }
}

export const updatePolicyFields = (
    dispatch: Dispatch<UpdateAction>,
    dispatchField: Dispatch<EditActions>,
    client: AppsClient | undefined,
    makeRequest: (
        grantPolicyId: string,
        revokePolicyId: string,
        certifyPolicyId: string
    ) => UpdateAppRequest | undefined,
): ((grantPolicyId: string, revokePolicyId: string, certifyPolicyId: string) => void) => {
    const doEdit = async (grantPolicyId: string, revokePolicyId: string, certifyPolicyId: string): Promise<void> => {
        if (!client) {
            return
        }
        dispatchField({
            type: RPCAction.edit,
            payload: {
                status: Status.LOADING,
            },
        })

        const req = makeRequest(grantPolicyId, revokePolicyId, certifyPolicyId)
        if (req === undefined) {
            return
        }

        let resp: UpdateAppResponse
        try {
            resp = await client.update(req).response
        } catch (err) {
            dispatchField({
                type: RPCAction.edit,
                payload: {
                    status: Status.ERROR,
                    error: convertError(err),
                },
            })
            return
        }
        dispatchField({
            type: RPCAction.edit,
            payload: {
                status: Status.OK,
            },
        })
        dispatch(convertUpdateAppPayload(resp))
    }

    return (grantPolicyId: string, revokePolicyId: string, certifyPolicyId: string): void => {
        doEdit(grantPolicyId, revokePolicyId, certifyPolicyId)
    }
}

export const updateCost = (
    dispatch: Dispatch<UpdateAction>,
    dispatchField: Dispatch<EditActions>,
    client: AppsClient | undefined,
    makeRequest: (cost: number) => UpdateAppRequest | undefined,
): ((cost: number) => void) => {
    const doEdit = async (cost: number): Promise<void> => {
        if (!client) {
            return
        }
        dispatchField({
            type: RPCAction.edit,
            payload: {
                status: Status.LOADING,
            },
        })

        const req = makeRequest(cost)
        if (req === undefined) {
            return
        }

        let resp: UpdateAppResponse
        try {
            resp = await client.update(req).response
        } catch (err) {
            dispatchField({
                type: RPCAction.edit,
                payload: {
                    status: Status.ERROR,
                    error: convertError(err),
                },
            })
            return
        }
        dispatchField({
            type: RPCAction.edit,
            payload: {
                status: Status.OK,
            },
        })
        dispatch(convertUpdateAppPayload(resp))
    }

    return (cost: number): void => {
        doEdit(cost)
    }
}

export const createApp = (
    dispatch: Dispatch<CreateAction>,
    client: AppsClient | undefined,
    displayName: string,
    description: string,
    ownerIds: string[],
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }

    const req = CreateAppRequest.create({
        displayName,
        description,
        owners: ownerIds,
    })
    return unaryInvoke<CreateAppRequest, CreateAppResponse>(req, client.create.bind(client), (payload) => {
        dispatch(convertCreateAppPayload(payload))
    })
}

export const generateAppPopulationReport = async (
    actionClient: AppReportActionServiceClient | undefined,
    appId: string,
): Promise<void> => {
    if (!actionClient) {
        return
    }
    await actionInvoke<AppActionsServiceGenerateReportRequest, Empty>(
        { appId },
        actionClient.generateReport.bind(actionClient),
    )
}

// TODO(kans): TS string type mapper thing so that we don't hard code the get...
type ClientLikeMethod<Req extends object, Res extends object> = (
    input: Req,
    options?: RpcOptions
) => UnaryCall<Req, Res>

export const unaryInvokePromise = <Req extends object, Res extends object>(
    method: ClientLikeMethod<Req, Res>,
    req: Req,
    dispatch?: Dispatch<ActionStatus<Res>>,
    notifyClient?: NotifyClient,
): Promise<Res> =>
        new Promise((resolve, reject) => {
            unaryInvoke<Req, Res>(
                req,
                method,
                (payload) => {
                    if (payload.status === Status.OK) {
                        resolve(payload.item)
                    }
                    if (payload.status === Status.ERROR) {
                        reject(payload.error)
                    }
                    dispatch?.(payload)
                },
                notifyClient,
            )
        })
